import { useCallback, useEffect, useState } from "react";

import { DollarSign } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";
import DocumentViewer from "../Photos/component/DocumentViewer";

import ContractorDialog from "./component/ContractorDialog";
import CostingForm from "./component/CostingForm";
import CostingTable from "./component/CostingTable";

const ContractorCosting = () => {
  const { user } = useAuth();
  const api = useApi();
  const { globalProjectId } = useData();
  const [showDeleted, setShowDeleted] = useState(false);

  // Modals state
  const [isCostingDialogOpen, setIsCostingDialogOpen] = useState(false);
  const [editingCosting, setEditingCosting] = useState(null);
  const [costingFormData, setCostingFormData] = useState({});
  const [isContractorDialogOpen, setIsContractorDialogOpen] = useState(false);

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");

  // Pagination for Costings
  const costingPagination = usePagination(10);
  const {
    items: costings,
    currentPage: costingPage,
    pageSize: costingPageSize,
    totalItems: costingTotalItems,
    setPaginationData: setCostingPaginationData,
    setCurrentPage: setCostingPage
  } = costingPagination;

  const fetchCostings = useCallback(
    async (page) => {
      try {
        setLoading(true);
        const response = await api.get(`/costings`, {
          params: {
            page,
            size: costingPageSize,
            projectId: globalProjectId === "ALL" ? undefined : globalProjectId,
            search: searchTerm.trim() || undefined,
            sortBy: sortBy || undefined,
            direction: direction || undefined,
            showDeleted
          }
        });
        setCostingPaginationData(response.data);
      } catch (error) {
        console.error("Error fetching costings:", error);
        toast.error("Failed to load costings");
      } finally {
        setLoading(false);
      }
    },
    [api, costingPageSize, globalProjectId, searchTerm, sortBy, direction, showDeleted, setCostingPaginationData]
  );

  useEffect(() => {
    fetchCostings(costingPage);
  }, [costingPage, fetchCostings, globalProjectId, sortBy, direction]);

  const handleOpenCostingDialog = (costing = null) => {
    if (costing) {
      setEditingCosting(costing);
      setCostingFormData({ ...costing });
    } else {
      setEditingCosting(null);
      setCostingFormData({
        projectId: globalProjectId === "ALL" ? "" : globalProjectId,
        contractorId: "",
        costingDate: new Date().toISOString(),
        remarks: "",
        documents: [],
        estimatedAmount: "",
        consumedAmount: ""
      });
    }
    setIsCostingDialogOpen(true);
  };

  const handleSaveCosting = async (e) => {
    e.preventDefault();
    try {
      if (editingCosting) {
        await api.put(`/costings/${editingCosting.id}`, costingFormData);
        toast.success("Costing record updated successfully");
      } else {
        await api.post(`/costings`, costingFormData);
        toast.success("Costing record created successfully");
      }
      setIsCostingDialogOpen(false);
      fetchCostings(costingPage);
    } catch (error) {
      toast.error(error.message || "Failed to save costing");
    }
  };

  const confirm = useConfirm();

  const handleDeleteCosting = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Costing Record",
      message: "Are you sure you want to delete this costing record?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/costings/${id}`);
      toast.success("Costing record deleted");
      fetchCostings(costingPage);
    } catch (error) {
      toast.error("Failed to delete costing record");
    }
  };

  const handleRefreshCosting = async (id) => {
    try {
      setLoading(true);
      await api.post(`/costings/${id}/refresh`);
      toast.success("Consumed amount refreshed successfully!");
      fetchCostings(costingPage);
    } catch (error) {
      toast.error(error.message || "Failed to refresh consumed amount");
    } finally {
      setLoading(false);
    }
  };

  const openViewer = (data) => {
    setViewerData(data);
    setIsViewerOpen(true);
  };

  // Permissions check
  const canRead = user?.permissions?.costing?.read;

  if (!canRead && user?.role !== "super_admin") {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-yale-blue-700 mb-2">Access Denied</h2>
        <p className="text-yale-blue-500">You don't have permission to access this section.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 flex flex-col pb-8">
        <PageHeader
          title="Costing"
          subtitle="Manage contractor costing allocations for projects"
          pageIcon={DollarSign}
          actionLabel={user?.permissions?.costing?.create ? "Add Costing" : null}
          onActionClick={() => handleOpenCostingDialog()}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput
                placeholder="Search costing..."
                className="md:w-72"
                onSearch={(val) => {
                  setSearchTerm(val);
                  setCostingPage(0);
                }}
              />
            </div>
          }
        />

        <div className="flex-1 flex flex-col mt-4">
          <CostingTable
            costings={costings}
            onEdit={handleOpenCostingDialog}
            onDelete={handleDeleteCosting}
            onViewImage={openViewer}
            onRefresh={handleRefreshCosting}
            permissions={{
              canUpdate: user?.permissions?.costing?.update,
              canDelete: user?.permissions?.costing?.delete
            }}
            sortBy={sortBy}
            direction={direction}
            onSort={(key, dir) => {
              setSortBy(key);
              setDirection(dir);
              setCostingPage(0);
            }}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setCostingPage={setCostingPage}
          />
        </div>

        <div className="mt-auto pt-6">
          <Pagination pagination={costingPagination} />
        </div>
      </div>

      {/* Costing Dialog */}
      <Dialog open={isCostingDialogOpen} onOpenChange={setIsCostingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>{editingCosting ? "Edit Costing Record" : "Add Costing Record"}</DialogTitle>
              <DialogDescription>Allocate costs to projects and contractors.</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleSaveCosting} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              <CostingForm
                formData={costingFormData}
                setFormData={setCostingFormData}
                onAddContractor={() => setIsContractorDialogOpen(true)}
              />
            </div>
            <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
              <Button type="submit" variant="primary" className="w-full py-6 rounded-lg shadow-lg">
                {editingCosting ? "Update Record" : "Save Record"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DocumentViewer isOpen={isViewerOpen} onClose={() => setIsViewerOpen(false)} data={viewerData} />

      <ContractorDialog
        isOpen={isContractorDialogOpen}
        onOpenChange={setIsContractorDialogOpen}
        onSuccess={(newContractor) => {
          setCostingFormData((prev) => ({
            ...prev,
            contractorId: newContractor.id
          }));
        }}
      />
    </div>
  );
};

export default ContractorCosting;
